import { useState } from "react";
import { Link, NavLink } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { useIsAdmin } from "../auth/useIsAdmin";
import { useCart } from "../cart/CartProvider";
import { useI18n } from "../i18n/I18nProvider";
import { useCatalog, useComingSoon, useStoreSales } from "../lib/CatalogProvider";
import { MarketCartButton } from "../market/MarketCartDrawer";
import { CurrencySwitcher } from "./CurrencySwitcher";
import { ThemeToggle } from "./ThemeToggle";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { NavMenu } from "./NavMenu";
import { SearchBar } from "./SearchBar";
import { CountBadge, useInbox } from "../market/InboxProvider";
import { GameSwitcher } from "./GameSwitcher";

export function Header() {
  const { t, pick } = useI18n();
  const { inbox } = useInbox();
  const { count, open } = useCart();
  const { user, signOut } = useAuth();
  const isAdmin = useIsAdmin();
  const comingSoon = useComingSoon();
  const storeSales = useStoreSales();
  // While "Próximamente", signing in is for admins and beta testers (the API refuses anyone else).
  const accountHref = user && !comingSoon ? "/cuenta" : "/ingresar";
  const { settings } = useCatalog();
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  return (
    <header className="site-header">
      {settings.announcement && <div className="announcement">{pick(settings.announcement)}</div>}
      <div className="header-main container">
        <button
          type="button"
          className="icon-btn menu-toggle"
          aria-expanded={menuOpen}
          aria-controls="main-nav"
          aria-label={menuOpen ? t("nav.close") : t("nav.menu")}
          onClick={() => setMenuOpen((o) => !o)}
        >
          <span className="burger" aria-hidden="true" />
        </button>
        <Link to="/" className="logo" onClick={closeMenu}>
          <picture>
            {/* Phones: icon only, so the header fits burger + logo + language + account + cart. */}
            <source
              media="(max-width: 639px)"
              srcSet="/brand/logo-mark.png 1x, /brand/logo-mark@2x.png 2x, /brand/logo-mark@3x.png 3x"
              width={37}
              height={40}
            />
            <img
              className="logo-img"
              src="/brand/logo-horizontal.png"
              srcSet="/brand/logo-horizontal.png 1x, /brand/logo-horizontal@2x.png 2x, /brand/logo-horizontal@3x.png 3x"
              width={222}
              height={48}
              alt="pokefans.fun"
            />
          </picture>
        </Link>
        <div className="header-search">
          <SearchBar onSubmitted={closeMenu} />
        </div>
        <div className="header-actions">
          <GameSwitcher className="game-switch-desktop" />
          <CurrencySwitcher />
          <LanguageSwitcher />
          <ThemeToggle />
          <Link
            to={accountHref}
            className="icon-btn account-btn"
            aria-label={user ? (inbox.total > 0 ? `${t("auth.account")}, ${t("inbox.label", { count: inbox.total })}` : t("auth.account")) : t("auth.login")}
            title={user ? user.name : t("auth.login")}
          >
            {user?.image ? (
              <img className="avatar-sm" src={user.image} alt="" width={28} height={28} referrerPolicy="no-referrer" />
            ) : (
              <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                <circle cx="12" cy="8" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
                <path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            )}
            {/* Something waiting in the market: a red count instead of the signed-in dot. */}
            {inbox.total > 0 ? (
              <span className="badge badge-alert" aria-hidden="true">
                {inbox.total > 99 ? "99+" : inbox.total}
              </span>
            ) : (
              user && !user.image && <span className="dot" aria-hidden="true" />
            )}
          </Link>
          {!comingSoon && !storeSales && <MarketCartButton />}
          {!comingSoon && storeSales && (
          <button type="button" className="icon-btn cart-btn" onClick={open} aria-label={t("cart.open", { count })}>
            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
              <path d="M3 4h2l2.2 10.2a2 2 0 0 0 2 1.6h7.6a2 2 0 0 0 2-1.5L21 8H6.2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
              <circle cx="10" cy="20" r="1.5" fill="currentColor" />
              <circle cx="17" cy="20" r="1.5" fill="currentColor" />
            </svg>
            {count > 0 && <span className="badge">{count}</span>}
          </button>
          )}
        </div>
      </div>
      <nav id="main-nav" className={menuOpen ? "main-nav is-open" : "main-nav"} aria-label={t("nav.menu")}>
        <div className="container nav-inner">
          <div className="nav-search">
            <SearchBar onSubmitted={closeMenu} />
          </div>
          <GameSwitcher className="game-switch-mobile" />
          <NavLink to="/" end onClick={closeMenu} className="nav-home">
            {t("nav.home")}
          </NavLink>
          <NavMenu onNavigate={closeMenu} />
          <span className="nav-account">
            {isAdmin && (
              <NavLink to="/admin" onClick={closeMenu} className="nav-admin">
                {t("nav.admin")}
              </NavLink>
            )}
            {user && !comingSoon && (
              <NavLink to="/cuenta" onClick={closeMenu}>
                {t("auth.account")} <CountBadge count={inbox.purchases + inbox.buyerOffers} />
              </NavLink>
            )}
            {/* Sellers: on wide screens too, next to the menu (the account icon is Mi cuenta). */}
            {user && !comingSoon && inbox.shop && (
              <NavLink to="/mi-tienda" onClick={closeMenu} className="nav-shop">
                {t("mkt.myShop")} <CountBadge count={inbox.sales + inbox.sellerOffers} />
              </NavLink>
            )}
            {user ? (
              <button type="button" className="nav-link-btn" onClick={() => (closeMenu(), void signOut())}>
                {t("auth.logout")}
              </button>
            ) : (
              <Link to={accountHref} onClick={closeMenu}>
                {t("auth.login")}
              </Link>
            )}
          </span>
        </div>
      </nav>
    </header>
  );
}
